/**
 * Arthaus live frame preview (Phase 10, HD-PD-FRAME)
 * docs/storefront-redesign/requirements/4_product_detail/HIGH_DESIGN_TRD.md
 *
 * Markup: the [data-hd-frame-stage] overlay + [data-hd-frame-thumb] in
 * sections/product-hero.liquid (snippets/hd-frame.liquid), data in
 * [data-hd-pdp-data]. Styles: assets/hd-frame.css.
 *
 * Listens for product-config.js's cancelable `pdp:variant-change`. When the
 * selected frame value has a profile, the flat print is re-dressed (moulding,
 * size proportions and caption animate via registered custom properties) and,
 * on a shopper change, the stage is shown and the event is cancelled so slide 0
 * keeps the room image instead of the variant's flat composite. Values without a
 * profile hide the stage and leave today's behaviour untouched.
 *
 * Flag: html[data-hd-frame-preview]. Re-initialises after a frame-switch swap
 * (`pdp:swapped`); listeners are bound once on the persistent hero element.
 */
window.ArthausHDFrame = window.ArthausHDFrame || (function () {
  'use strict';

  var state = null;

  function on() { return document.documentElement.hasAttribute('data-hd-frame-preview'); }

  // "50cm x 70cm (20'' x 28'')" → { w: 500, h: 700 } in mm; null when unparseable.
  function sizeMm(value) {
    var m = String(value || '').match(/(\d+(?:\.\d+)?)\s*cm\s*[x×]\s*(\d+(?:\.\d+)?)\s*cm/i);
    return m ? { w: parseFloat(m[1]) * 10, h: parseFloat(m[2]) * 10 } : null;
  }

  // ── Artwork source: deferred, then trimmed ────────────────────────────────
  // The flat print photo sits on a white canvas with a margin around the sheet.
  // Trimming to the sheet's faint edge (min channel < 252) leaves exactly the print
  // as sold (art + printed white border), which the moulding then wraps. Loaded on
  // idle or first intent so it never competes with the LCP image.
  function pickSrc(img) {
    var set = (img.getAttribute('data-hd-srcset') || '').split(',').map(function (s) {
      var p = s.trim().split(/\s+/);
      return { url: p[0], w: parseInt(p[1], 10) || 0 };
    }).filter(function (c) { return c.url; }).sort(function (a, b) { return a.w - b.w; });
    var need = (img.getBoundingClientRect().width || 500) * (window.devicePixelRatio || 1);
    for (var i = 0; i < set.length; i++) if (set[i].w >= need) return set[i].url;
    return set.length ? set[set.length - 1].url : img.getAttribute('data-hd-src');
  }

  function trimBox(img) {
    var W = 400, H = Math.max(1, Math.round(W * img.naturalHeight / img.naturalWidth));
    var c = document.createElement('canvas');
    c.width = W; c.height = H;
    var ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, W, H);
    var d = ctx.getImageData(0, 0, W, H).data;           // throws if the image is tainted
    var x0 = W, y0 = H, x1 = -1, y1 = -1;
    for (var y = 0; y < H; y++) {
      for (var x = 0; x < W; x++) {
        var i = (y * W + x) * 4;
        if (Math.min(d[i], d[i + 1], d[i + 2]) < 252) {
          if (x < x0) x0 = x; if (x > x1) x1 = x;
          if (y < y0) y0 = y; if (y > y1) y1 = y;
        }
      }
    }
    // Shave the sheet's faint edge line itself so letterboxing (photo vs size aspect) reads as clean border.
    x0 += 2; y0 += 2; x1 -= 2; y1 -= 2;
    var bw = x1 - x0 + 1, bh = y1 - y0 + 1;
    // Only trim a real margin; a full-bleed photo or an odd result keeps the original.
    if (x1 < 0 || bw < W * 0.4 || bh < H * 0.4 || (bw > W * 0.97 && bh > H * 0.97)) return null;
    var k = img.naturalWidth / W;
    return { x: x0 * k, y: y0 * k, w: bw * k, h: bh * k };
  }

  function loadArt() {
    if (!state || state.artLoading) return;
    state.artLoading = true;
    var imgs = state.figures.map(function (f) { return f.querySelector('.hd-frame__art'); }).filter(Boolean);
    var main = state.stage.querySelector('.hd-frame__art') || imgs[0];
    if (!main) return;
    var fallback = function () {
      imgs.forEach(function (im) {
        im.setAttribute('srcset', im.getAttribute('data-hd-srcset') || '');
        im.setAttribute('src', im.getAttribute('data-hd-src') || '');
      });
      reveal();
    };
    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onerror = fallback;
    probe.onload = function () {
      var box = null;
      try { box = trimBox(probe); } catch (e) { box = null; }
      if (!box || !window.HTMLCanvasElement || !HTMLCanvasElement.prototype.toBlob) { fallback(); return; }
      var c = document.createElement('canvas');
      c.width = Math.round(box.w); c.height = Math.round(box.h);
      c.getContext('2d').drawImage(probe, box.x, box.y, box.w, box.h, 0, 0, c.width, c.height);
      c.toBlob(function (blob) {
        if (!blob) { fallback(); return; }
        var url = URL.createObjectURL(blob);
        imgs.forEach(function (im) {
          im.removeAttribute('srcset');
          im.width = c.width; im.height = c.height;
          im.src = url;
        });
        if (state) { state.artAspect = c.width / c.height; fit(); }
        reveal();
      }, 'image/jpeg', 0.9);
    };
    probe.src = pickSrc(main);
  }

  // The trimmed sheet's aspect rarely matches every size exactly. Close matches fill
  // the frame (cover trims a sliver of the printed border, never the art: the border
  // is ~14% a side, cover at most ~7%); larger mismatches letterbox in white (contain).
  function fit() {
    if (!state || !state.artAspect) return;
    var fig = state.figures[0];
    var w = parseFloat(fig.style.getPropertyValue('--art-w')) || 0;
    var h = parseFloat(fig.style.getPropertyValue('--art-h')) || 0;
    if (!w || !h) return;
    var r = (w / h) / state.artAspect;
    var mode = r > 0.85 && r < 1.15 ? 'cover' : 'contain';
    state.figures.forEach(function (f) {
      var im = f.querySelector('.hd-frame__art');
      if (im) im.style.objectFit = mode;
    });
  }

  function reveal() {
    if (state && state.thumb) state.thumb.hidden = false;
  }

  function scheduleArt() {
    var go = function () { loadArt(); };
    var idle = function () {
      if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 3000 });
      else setTimeout(go, 1200);
    };
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  function readData(hero) {
    var el = hero.querySelector('[data-hd-pdp-data]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function thumbs(hero) { return hero.querySelectorAll('[data-thumb-index]'); }

  function setActive(active) {
    if (!state) return;
    state.stage.classList.toggle('is-active', active);
    state.stage.setAttribute('aria-hidden', active ? 'false' : 'true');
    if (state.thumb) state.thumb.setAttribute('aria-pressed', String(active));
    if (active) {
      // The regular thumbs track their own selection in product-config.js; mirror
      // "none of you is showing" visually while the preview is up.
      thumbs(state.hero).forEach(function (t) {
        t.setAttribute('aria-pressed', 'false');
        t.classList.remove('border-charcoal');
        t.classList.add('border-transparent');
      });
    }
  }

  function captionFor(profile, opts) {
    var d = state.data;
    var text = profile.stated ? profile.name + ' · ' + profile.mm + ' mm moulding' : profile.name + ' frame';
    if (d.borderPos > 0 && opts[d.borderPos - 1]) text += ' · ' + String(opts[d.borderPos - 1]).toLowerCase();
    return text;
  }

  function apply(variant) {
    var d = state.data;
    var opts = variant.options || [];
    var profile = d.profiles && d.profiles[opts[d.framePos - 1]];
    if (!profile) return null;

    var size = d.sizePos > 0 ? sizeMm(opts[d.sizePos - 1]) : null;
    if (size && d.landscape && size.w < size.h) size = { w: size.h, h: size.w };

    state.figures.forEach(function (fig) {
      fig.setAttribute('data-frame', profile.key);
      fig.style.setProperty('--profile-mm', profile.key === 'none' ? 0 : profile.mm);
      if (size) {
        fig.style.setProperty('--art-w', size.w);
        fig.style.setProperty('--art-h', size.h);
      }
    });
    fit();
    var caption = captionFor(profile, opts);
    if (state.caption) state.caption.textContent = caption;
    var img = state.stage.querySelector('.hd-frame__art');
    if (img) img.alt = d.title + ' in a ' + caption;
    return { profile: profile, caption: caption };
  }

  function announce(text) {
    if (!state.live) return;
    state.live.textContent = '';
    // Next frame, so a repeated message is still announced.
    requestAnimationFrame(function () { state.live.textContent = 'Preview: ' + text; });
  }

  function onVariantChange(e) {
    if (!state || !on() || !e.detail || !e.detail.variant) return;
    var result = apply(e.detail.variant);
    if (!result) { setActive(false); return; }       // no profile → today's behaviour
    if (!e.detail.isUser) return;
    e.preventDefault();                                // keep the room image in slide 0
    loadArt();                                         // no-op once loaded
    setActive(true);
    announce(result.caption);
    if (window.ahPosthog) {
      window.ahPosthog('hd_frame', 'preview', {
        event_category: 'engagement', profile: result.profile.key, handle: state.data.title
      });
    }
  }

  function bindOnce(hero) {
    if (hero._hdFrameBound) return;
    hero._hdFrameBound = true;
    hero.addEventListener('pdp:variant-change', onVariantChange);
    hero.addEventListener('click', function (e) {
      if (!state) return;
      if (e.target.closest('[data-hd-frame-thumb]')) { setActive(true); return; }
      if (e.target.closest('[data-thumb-index]')) { setActive(false); return; }
      // The stage sits inside the lightbox trigger; the CSS preview isn't a lightbox image.
      if (e.target.closest('[data-hd-frame-stage].is-active')) e.stopPropagation();
    }, true);
  }

  function init() {
    state = null;
    if (!on()) return;
    var hero = document.querySelector('[data-product-hero]');
    if (!hero) return;
    var data = readData(hero);
    var stage = hero.querySelector('[data-hd-frame-stage]');
    if (!data || !data.framePos || !stage) return;

    var live = hero.querySelector('[data-hd-frame-live]');
    if (!live) {
      live = document.createElement('p');
      live.className = 'sr-only';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('data-hd-frame-live', '');
      hero.appendChild(live);
    }
    state = {
      hero: hero,
      data: data,
      stage: stage,
      thumb: hero.querySelector('[data-hd-frame-thumb]'),
      figures: Array.prototype.slice.call(hero.querySelectorAll('[data-hd-frame-figure]')),
      caption: stage.querySelector('[data-hd-frame-caption]'),
      live: live
    };
    stage.hidden = false;                               // still invisible until .is-active (CSS)
    bindOnce(hero);
    // The thumbnail is revealed once the artwork is ready (loadArt → reveal).
    var intent = hero.querySelector('[data-product-config]');
    if (intent) {
      var early = function () { loadArt(); };
      intent.addEventListener('pointerenter', early, { once: true });
      intent.addEventListener('focusin', early, { once: true });
    }
    scheduleArt();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  window.addEventListener('pdp:swapped', init);

  return { init: init, sizeMm: sizeMm };
})();
